import { ReactNode, useEffect, useRef } from 'react'
import { Grid, P } from '@farewill/ui'
import { useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import { AnySchema } from 'yup'

import DateSplit from 'components/form/date-split'
import InputFloating from 'components/form/input-floating'
import { splitIsoDate } from 'components/form/helpers'
import { Option } from 'components/form/select-input/types'
import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import { Address as AddressType } from 'lib/models/address'
import { formatValuesForFormik } from 'utils/helpers'

import { FieldsGrid } from '../components/collection-section'
import PersonNameFields from '../components/person-name-fields'
import NotesField from '../components/notes-field'
import { RELATIONSHIP_LABEL, relationshipOptions } from '../options'
import ContactSelect from './contact-select'
import { PersonAttributes, useEstatePeople } from './estate-people'
import { hasBothNames, NEW_PERSON } from './helpers'
import { SharedPersonRecord } from './use-shared-people'

type HandleSave = (event: {
  name: string
  value: string | number | null
}) => void

export type RenderPersonFields = (fieldProps: {
  handleSave: HandleSave
  values: Record<string, unknown>
  // someone new has no address saved yet, so it's kept until they are
  onUnsavedAddressChange?: (address: AddressType) => void
}) => ReactNode

const hasAnyValue = (fields: unknown): fields is object =>
  !!fields && Object.values(fields).some(Boolean)

const toDraftAttributes = (
  values: Record<string, unknown>,
  schema: AnySchema
): PersonAttributes => {
  const { address, ...fields } = pick(
    values,
    Object.keys((schema as unknown as { fields: object }).fields)
  )
  return {
    ...pickBy(fields, (value) => value !== '' && value !== undefined),
    ...(hasAnyValue(address) && { address }),
  } as PersonAttributes
}

const toFormValues = (record: SharedPersonRecord): Record<string, unknown> => {
  const dateOfBirth = splitIsoDate(record.attributes.dateOfBirth ?? null)
  return formatValuesForFormik({
    ...record.attributes,
    contact: record.isDraft ? NEW_PERSON : record.id,
    dateOfBirthDay: dateOfBirth.day,
    dateOfBirthMonth: dateOfBirth.month,
    dateOfBirthYear: dateOfBirth.year,
  })
}

/**
 * The same person can be in more than one card. Only the fields that
 * changed are copied in, so what's being typed elsewhere is left alone.
 */
const KeepInStep = ({
  savedValues,
}: {
  savedValues: Record<string, unknown>
}) => {
  const { setFieldValue } = useFormikContext()
  const previous = useRef(savedValues)

  useEffect(() => {
    Object.entries(savedValues).forEach(([name, value]) => {
      if (!isEqual(previous.current[name], value))
        setFieldValue(name, value, false)
    })
    previous.current = savedValues
  }, [savedValues, setFieldValue])

  return null
}

type Props = {
  record: SharedPersonRecord
  options: Option[]
  schema: AnySchema
  onChoose: (value: string | number) => unknown
  // resolves with the new person's id
  onSaveDraft: (attributes: PersonAttributes) => Promise<number>
  renderFields?: RenderPersonFields
  compact?: boolean
}

const SharedPersonFields = ({
  record,
  options,
  schema,
  onChoose,
  onSaveDraft,
  renderFields,
  compact = false,
}: Props) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>()
  const { updatePerson } = useEstatePeople()
  const savedValues = toFormValues(record)
  const isSavingDraft = useRef(false)
  // what's changed since the draft was sent, saved to them once they exist
  const changedWhileSaving = useRef<PersonAttributes>({})

  const saveDraft = (changes: Record<string, unknown>) => {
    const draft = toDraftAttributes({ ...values, ...changes }, schema)
    if (!hasBothNames(draft)) return

    if (isSavingDraft.current) {
      changedWhileSaving.current = {
        ...changedWhileSaving.current,
        ...toDraftAttributes(changes, schema),
      }
      return
    }

    isSavingDraft.current = true
    return Promise.resolve(onSaveDraft(draft))
      .then((personId) => {
        const later = changedWhileSaving.current
        if (Object.keys(later).length) return updatePerson(personId, later)
      })
      .finally(() => {
        isSavingDraft.current = false
        changedWhileSaving.current = {}
      })
  }

  const handleSave = useValidatedSave({
    schema,
    savedValues,
    save: record.isDraft
      ? ({ name, value }) => saveDraft({ [name]: value })
      : ({ name, value }) => updatePerson(record.id, { [name]: value }),
  })

  const onUnsavedAddressChange = (address: AddressType) => {
    setFieldValue('address', address)
    saveDraft({ address })
  }

  return (
    <>
      {!record.isDraft && <KeepInStep savedValues={savedValues} />}
      <FieldsGrid gap="S">
        <Grid.Item span={12}>
          <ContactSelect
            options={options}
            hideSelectedDateOfBirth={!compact}
            onChoose={onChoose}
          />
        </Grid.Item>
        <PersonNameFields handleSave={handleSave} />
        <Grid.Item span={12}>
          <InputFloating
            name="relationshipToDeceased"
            label={RELATIONSHIP_LABEL}
            component="select"
            options={relationshipOptions(values.relationshipToDeceased)}
            handleSave={handleSave}
          />
        </Grid.Item>
        {record.isDraft && (
          <Grid.Item span={12}>
            <P size="S" margin={0}>
              We’ll save them once both their names are filled in.
            </P>
          </Grid.Item>
        )}
        {!compact && (
          <>
            <Grid.Item span={12}>
              <DateSplit
                composeParts
                name="dateOfBirth"
                label="Date of birth"
                handleSave={handleSave}
              />
            </Grid.Item>
            {renderFields?.({
              handleSave,
              values,
              ...(record.isDraft && { onUnsavedAddressChange }),
            })}
          </>
        )}
      </FieldsGrid>
      {!compact && <NotesField name="notes" handleSave={handleSave} />}
    </>
  )
}

const SharedPersonForm = withForm(SharedPersonFields)

const SharedPersonCard = (props: Props) => (
  <SharedPersonForm
    initialValues={toFormValues(props.record)}
    validationSchema={props.schema}
    {...props}
  />
)

export default SharedPersonCard
